import React, { useMemo, useState, useEffect, useContext, useRef } from "react";
import { Offcanvas, Nav, Form, Button, Badge } from "react-bootstrap";
import StarekContext from "../../context/StarekContext";
import { gql, useQuery, useMutation } from "@apollo/client";
import moment from "moment-timezone";

const GET_PLANTS_LIST = gql`
    query GetPlants {
        plants {
            id
            name
        }
    }
`;

const GET_ROLES_LIST = gql`
    query GetRoles {
        roles {
            id
            name
        }
    }
`;

const CREATE_USER_WITH_IDENTITY = gql`
    mutation CreateUserWithIdentity($data: UserCreateInput!) {
        createOneUser(data: $data) {
            id
            username
            name
            email
            verified
            is_active
            description
            home_timezone
            current_timezone
            show_time_in_home_timezone
            created_at
            updated_at
            identities {
                id
                provider
                subject
                email
                linked_at
            }
            memberships {
                plant {
                    id
                    name
                }
                role {
                    id
                    name
                }
            }
        }
    }
`;

const UPDATE_USER = gql`
    mutation UpdateUser(
        $where: UserWhereUniqueInput!
        $data: UserUpdateInput!
    ) {
        updateOneUser(where: $where, data: $data) {
            id
            username
            name
            email
            verified
            is_active
            description
            home_timezone
            current_timezone
            show_time_in_home_timezone
            updated_at
            memberships {
                plant {
                    id
                    name
                }
                role {
                    id
                    name
                }
            }
        }
    }
`;

const Tag = ({ text, right, onRemove }) => (
    <div className="d-inline-flex align-items-center border rounded px-2 py-1 me-2 mb-2 bg-light">
        <span className="me-2">{text}</span>
        {right && (
            <Badge bg="light" text="dark" className="border me-2">
                {right}
            </Badge>
        )}
        {onRemove && (
            <i
                className="fa fa-times text-muted cursor-pointer"
                onClick={onRemove}
            />
        )}
    </div>
);

const AddUserSidebar = ({
    show = true,
    mode = "add",
    initialValues = {},
    onClose = () => {},
    onFinish = () => {},
}) => {
    const TIMEZONES = useMemo(() => {
        let names = moment.tz.names();
        const legacyAliases = ["Asia/Calcutta"];
        for (const z of legacyAliases) {
            if (initialValues?.home_timezone === z && !names.includes(z)) {
                names = [z, ...names];
            }
        }
        return names;
    }, [initialValues?.home_timezone]);

    const [activeTab, setActiveTab] = useState("basic");
    const [saving, setSaving] = useState(false);

    const [name, setName] = useState(initialValues.name || "");
    const [email, setEmail] = useState(initialValues.email || "");
    const [username, setUsername] = useState(initialValues.username || "");
    const [password, setPassword] = useState(""); // NEW: user-provided password
    const [description, setDescription] = useState(
        initialValues.description || ""
    );
    const [homeTz, setHomeTz] = useState(
        initialValues.home_timezone || "Asia/Kolkata"
    );
    const [alwaysHomeTz, setAlwaysHomeTz] = useState(
        !!initialValues.show_time_in_home_timezone
    );

    const linkedRef = useRef(null);
    const [linked, setLinked] = useState([]);
    const [membershipsDirty, setMembershipsDirty] = useState(false); // NEW: only update memberships if changed

    const [selPlant, setSelPlant] = useState("");
    const [selRole, setSelRole] = useState("");

    // const [permPlant, setPermPlant] = useState(
    //   initialValues.permissions?.plant || ""
    // );
    // const [permSubscription, setPermSubscription] = useState(PERM_ENTITY[2]);
    // const [permZone, setPermZone] = useState(PERM_ENTITY[0]);
    // const [permDevice, setPermDevice] = useState(PERM_ASSOC[0]);
    // const [permReport, setPermReport] = useState(PERM_ACTION[0]);
    // const [permEmail, setPermEmail] = useState(PERM_ACTION[0]);
    const starekContext = useContext(StarekContext);
    const headerTitle = useMemo(
        () => (mode === "edit" ? "Edit User" : "Add User"),
        [mode]
    );
    const [usernameChanged, setUsernameChanged] = useState(false);
    const [currentTz, setCurrentTz] = useState(
        Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata"
    );

    const {
        data: plantData,
        loading: plantsLoading,
        error: plantsError,
        refetch: refetchPlants,
    } = useQuery(GET_PLANTS_LIST, { client: starekContext.engineClient });

    const {
        data: roleData,
        loading: rolesLoading,
        error: rolesError,
        refetch: refetchRoles,
    } = useQuery(GET_ROLES_LIST, { client: starekContext.engineClient });

    const [createUser, { loading: creating }] = useMutation(
        CREATE_USER_WITH_IDENTITY,
        { client: starekContext.engineClient }
    );
    const [updateUser, { loading: updating }] = useMutation(UPDATE_USER, {
        client: starekContext.engineClient,
    });

    const plantOptions = useMemo(() => plantData?.plants ?? [], [plantData]);
    const roleOptions = useMemo(() => roleData?.roles ?? [], [roleData]);

    const set = (v) => ({ set: v });
    const setNullable = (v) => ({ set: v ?? null });

    const toMembershipCreates = (arr) =>
        (arr || [])
            .filter((x) => x?.plantId && x?.roleId)
            .map((x) => ({
                plant: {
                    connect: { id: Number.parseInt(String(x.plantId), 10) },
                },
                role: {
                    connect: { id: Number.parseInt(String(x.roleId), 10) },
                },
            }));

    // VALIDATION:
    const passwordOk = mode === "edit" ? true : password.trim().length > 0;
    const createReady =
        linked.length > 0 && passwordOk && !saving && !creating && !updating;

    // useEffect(() => {
    //   if (!plantOptions.length) return;
    //   setPermPlant((current) => current || plantOptions[0].name);
    // }, [plantOptions]);

    useEffect(() => {
        setCurrentTz(
            Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata"
        );
    }, []);

    const slugify = (s) =>
        (s || "")
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "")
            .slice(0, 30);

    useEffect(() => {
        if (mode !== "edit" && !usernameChanged) setUsername(slugify(name));
    }, [name, usernameChanged, mode]);

    // RESET FIELDS + ALWAYS OPEN TO BASIC
    useEffect(() => {
        setName(initialValues.name || "");
        setEmail(initialValues.email || "");
        setUsername(initialValues.username || "");
        setPassword(""); // never fetch or prefill existing password
        setDescription(initialValues.description || "");
        setHomeTz(initialValues.home_timezone || "Asia/Kolkata");
        setAlwaysHomeTz(!!initialValues.show_time_in_home_timezone);
        setUsernameChanged(false);

        const fromServer = Array.isArray(initialValues.memberships)
            ? initialValues.memberships.map((m) => ({
                  plant: m?.plant?.name,
                  plantId: m?.plant?.id,
                  role: m?.role?.name,
                  roleId: m?.role?.id,
              }))
            : [];
        setLinked(fromServer);
        setMembershipsDirty(false); // pristine on open
        setActiveTab("basic"); // NEW: always open on basic
    }, [mode, show, initialValues]);

    const handleAddPending = () => {
        if (!selPlant || !selRole) return;
        const selectedPlant = plantOptions.find((p) => p.name === selPlant);
        const selectedRole = roleOptions.find((r) => r.name === selRole);
        setLinked((l) => [
            ...l,
            {
                plant: selectedPlant?.name || selPlant,
                plantId: selectedPlant?.id,
                role: selectedRole?.name || selRole,
                roleId: selectedRole?.id,
            },
        ]);
        setSelPlant("");
        setSelRole("");
        setMembershipsDirty(true); // mark changed
        setTimeout(
            () =>
                linkedRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                }),
            0
        );
    };

    const handleRemoveLinked = (idx) => {
        setLinked((l) => {
            const next = l.filter((_, i) => i !== idx);
            return next;
        });
        setMembershipsDirty(true); // mark changed
    };

    const submit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setSaving(true);
        try {
            const actorIdRaw = starekContext.user.id;
            const actorId = Number.isInteger(actorIdRaw)
                ? actorIdRaw
                : Number.parseInt(String(actorIdRaw), 10);
            if (!actorId) throw new Error("No user id found.");

            if (mode === "edit") {
                const targetId = Number.parseInt(String(initialValues.id), 10);

                // Build update payload with ONLY allowed fields
                const data = {
                    // name: NOT allowed in edit
                    // email/username: NOT allowed in edit
                    description: setNullable(description || null),
                    home_timezone: set(homeTz || "Asia/Kolkata"),
                    current_timezone: setNullable(currentTz || null),
                    show_time_in_home_timezone: set(!!alwaysHomeTz),
                    updated_by: { connect: { id: actorId } },
                };

                // Update memberships ONLY if user visited/changed them
                if (membershipsDirty) {
                    data.memberships = {
                        deleteMany: {}, // clear and recreate based on current 'linked'
                        create: toMembershipCreates(linked),
                    };
                }

                // Update local identity password if provided (leave blank = no change)
                if (password.trim()) {
                    data.identities = {
                        updateMany: [
                            {
                                where: {
                                    provider: {
                                        equals: "local-identity-provider",
                                    },
                                },
                                data: { idp: { password: password.trim() } },
                            },
                        ],
                    };
                }

                const res = await updateUser({
                    variables: { where: { id: targetId }, data },
                });
                onFinish?.(res?.data?.updateOneUser);
            } else {
                // Create validations
                if (!passwordOk) throw new Error("Please set a password.");
                if (linked.length === 0)
                    throw new Error("Please add at least one membership.");

                const res = await createUser({
                    variables: {
                        data: {
                            username,
                            name,
                            email,
                            description,
                            verified: false,
                            is_active: true,
                            home_timezone: homeTz,
                            current_timezone: currentTz,
                            show_time_in_home_timezone: alwaysHomeTz,
                            identities: {
                                create: [
                                    {
                                        provider: "local-identity-provider",
                                        subject: username,
                                        email,
                                        idp: {
                                            // pass user-entered password (server should hash)
                                            password: password.trim(),
                                        },
                                    },
                                ],
                            },
                            memberships: {
                                create: toMembershipCreates(linked),
                            },
                            created_by: { connect: { id: actorId } },
                            updated_by: { connect: { id: actorId } },
                        },
                    },
                });
                onFinish?.(res?.data?.createOneUser);
            }
        } catch (err) {
            console.error(err);
            alert(err.message || "Failed to save user");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Offcanvas
            show={show}
            onHide={onClose}
            placement="end"
            backdrop="static"
        >
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>{headerTitle}</Offcanvas.Title>
            </Offcanvas.Header>

            <Offcanvas.Body className="pt-0">
                <Nav
                    variant="tabs"
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k || "basic")}
                    className="mb-3"
                >
                    <Nav.Item>
                        <Nav.Link eventKey="basic">Basic Details</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="memberships">Memberships</Nav.Link>
                    </Nav.Item>
                    {/* <Nav.Item>
            <Nav.Link eventKey="permissions">Additional Permissions</Nav.Link>
          </Nav.Item> */}
                </Nav>

                {activeTab === "basic" && (
                    <Form onSubmit={submit}>
                        <div className="d-flex justify-content-center my-3">
                            <div
                                className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                                style={{
                                    width: 160,
                                    height: 160,
                                    border: "2px dashed #ddd",
                                }}
                            >
                                <i className="fa fa-user fa-3x text-muted" />
                            </div>
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                placeholder="Full name"
                                disabled={mode === "edit"} // EDIT: cannot change
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                Email <span className="text-danger">*</span>
                            </Form.Label>
                            <div className="d-flex">
                                <Form.Control
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    type="email"
                                    required
                                    disabled={mode === "edit"} // EDIT: cannot change
                                />
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                Username <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                value={username}
                                onChange={(e) => {
                                    setUsernameChanged(true);
                                    setUsername(e.target.value);
                                }}
                                required
                                placeholder="Username (auto from name; editable)"
                                disabled={mode === "edit"} // EDIT: cannot change
                            />
                            {mode !== "edit" && (
                                <Form.Text className="text-muted">
                                    Auto-generated from Name; you can change it.
                                </Form.Text>
                            )}
                        </Form.Group>

                        {/* NEW: Password */}
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Password{" "}
                                {mode !== "edit" && (
                                    <span className="text-danger">*</span>
                                )}
                            </Form.Label>
                            <Form.Control
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={
                                    mode === "edit"
                                        ? "You can reset your password here"
                                        : "Set a password"
                                }
                            />
                            <Form.Text className="text-muted">
                                We don't fetch or display existing passwords.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Write a short note…"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                Home Time Zone{" "}
                                <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Select
                                value={homeTz}
                                onChange={(e) => setHomeTz(e.target.value)}
                            >
                                {TIMEZONES.map((tz) => (
                                    <option key={tz}>{tz}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Check
                                type="checkbox"
                                label={
                                    <div>
                                        <strong>
                                            Always Use Home Timezone
                                        </strong>
                                        <div className="text-danger small">
                                            Checking this box will override
                                            browser time zone – Not Recommended
                                        </div>
                                    </div>
                                }
                                checked={alwaysHomeTz}
                                onChange={(e) =>
                                    setAlwaysHomeTz(e.target.checked)
                                }
                            />
                        </Form.Group>
                    </Form>
                )}

                {activeTab === "memberships" && (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Plant <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Select
                                value={selPlant}
                                onChange={(e) => setSelPlant(e.target.value)}
                                disabled={
                                    plantsLoading || plantOptions.length === 0
                                }
                            >
                                <option value="">
                                    {plantsLoading ? "Loading..." : "Select"}
                                </option>
                                {plantOptions.map((plant) => (
                                    <option key={plant.id} value={plant.name}>
                                        {plant.name}
                                    </option>
                                ))}
                            </Form.Select>
                            {plantsError && (
                                <div className="text-danger small mt-1">
                                    Unable to load plants.
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0 ms-1 align-baseline"
                                        onClick={() => refetchPlants()}
                                    >
                                        Retry
                                    </Button>
                                </div>
                            )}
                            {!plantsLoading &&
                                !plantsError &&
                                plantOptions.length === 0 && (
                                    <div className="text-muted small mt-1">
                                        No plants available.
                                    </div>
                                )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                Role <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Select
                                value={selRole}
                                onChange={(e) => setSelRole(e.target.value)}
                                disabled={
                                    rolesLoading || roleOptions.length === 0
                                }
                            >
                                <option value="">
                                    {rolesLoading ? "Loading..." : "Select"}
                                </option>
                                {roleOptions.map((role) => (
                                    <option key={role.id} value={role.name}>
                                        {role.name}
                                    </option>
                                ))}
                            </Form.Select>
                            {rolesError && (
                                <div className="text-danger small mt-1">
                                    Unable to load roles.
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0 ms-1 align-baseline"
                                        onClick={() => refetchRoles()}
                                    >
                                        Retry
                                    </Button>
                                </div>
                            )}
                            {!rolesLoading &&
                                !rolesError &&
                                roleOptions.length === 0 && (
                                    <div className="text-muted small mt-1">
                                        No roles available.
                                    </div>
                                )}
                        </Form.Group>

                        <div className="d-flex justify-content-end mb-3">
                            <Button
                                variant="outline-primary"
                                onClick={handleAddPending}
                                disabled={!selPlant || !selRole}
                            >
                                Add
                            </Button>
                        </div>

                        <div className="text-muted small mb-1" ref={linkedRef}>
                            Linked Plants
                        </div>
                        <div className="mb-3">
                            {linked.length === 0 && (
                                <div className="text-muted">None</div>
                            )}
                            {linked.map((t, i) => (
                                <Tag
                                    key={i}
                                    text={t.plant}
                                    right={t.role}
                                    onRemove={() => handleRemoveLinked(i)}
                                />
                            ))}
                        </div>
                    </>
                )}

                {/* {activeTab === "permissions" && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>
                Plant <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={permPlant}
                onChange={(e) => setPermPlant(e.target.value)}
              >
                {plantOptions.map((p) => (
                  <option key={p.id} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <div className="text-muted mb-2">Additional Permissions</div>

            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between">
                <Form.Label>
                  Subscription <span className="text-danger">*</span>
                </Form.Label>
                <Badge bg="light" text="dark" className="border">
                  Entity
                </Badge>
              </div>
              <Form.Select
                value={permSubscription}
                onChange={(e) => setPermSubscription(e.target.value)}
              >
                {PERM_ENTITY.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between">
                <Form.Label>
                  Zone <span className="text-danger">*</span>
                </Form.Label>
                <Badge bg="light" text="dark" className="border">
                  Entity
                </Badge>
              </div>
              <Form.Select
                value={permZone}
                onChange={(e) => setPermZone(e.target.value)}
              >
                {PERM_ENTITY.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between">
                <Form.Label>
                  Device <span className="text-danger">*</span>
                </Form.Label>
                <Badge bg="light" text="dark" className="border">
                  Association
                </Badge>
              </div>
              <Form.Select
                value={permDevice}
                onChange={(e) => setPermDevice(e.target.value)}
              >
                {PERM_ASSOC.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between">
                <Form.Label>
                  Report <span className="text-danger">*</span>
                </Form.Label>
                <Badge bg="light" text="dark" className="border">
                  Action
                </Badge>
              </div>
              <Form.Select
                value={permReport}
                onChange={(e) => setPermReport(e.target.value)}
              >
                {PERM_ACTION.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between">
                <Form.Label>
                  Send Email <span className="text-danger">*</span>
                </Form.Label>
                <Badge bg="light" text="dark" className="border">
                  Action
                </Badge>
              </div>
              <Form.Select
                value={permEmail}
                onChange={(e) => setPermEmail(e.target.value)}
              >
                {PERM_ACTION.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </>
        )} */}
            </Offcanvas.Body>

            {/* Footer buttons */}
            <div className="d-flex justify-content-between align-items-center px-3 pb-3">
                <Button variant="outline-secondary" onClick={onClose}>
                    + Discard
                </Button>

                {activeTab === "basic" ? (
                    mode === "edit" ? (
                        <div className="d-flex" style={{ gap: 8 }}>
                            <Button
                                variant="outline-primary"
                                onClick={submit}
                                disabled={saving || updating}
                            >
                                {saving || updating ? "Saving…" : "Save"}
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => setActiveTab("memberships")}
                            >
                                Next
                            </Button>
                        </div>
                    ) : (
                        <Button
                            variant="primary"
                            onClick={() => setActiveTab("memberships")}
                        >
                            Next
                        </Button>
                    )
                ) : (
                    <div className="d-flex" style={{ gap: 8 }}>
                        <Button
                            variant="outline-primary"
                            onClick={() => setActiveTab("basic")}
                        >
                            Back
                        </Button>
                        <Button
                            variant="primary"
                            onClick={submit}
                            disabled={
                                mode === "add"
                                    ? !createReady
                                    : saving || creating || updating
                            }
                        >
                            {saving || creating || updating
                                ? mode === "edit"
                                    ? "Updating…"
                                    : "Creating…"
                                : mode === "edit"
                                ? "Update"
                                : "Create"}
                        </Button>
                    </div>
                )}
            </div>
        </Offcanvas>
    );
};

export default AddUserSidebar;
